import { FC } from "react";

import { TaskDriverStatusEnum } from "@/domain/models/common";
import { useTranslator } from "@/i18n/useTranslator";

import {
  DriverFlowBottomButton,
  JobSiteLabel,
  PhoneCallButton,
} from "../blocks";
import { DsDataDisplay, DsDivider, DsText } from "../components/ui";
import { useUpdateServiceMutation } from "../data/mutations/serviceMutations";
import { useUpdateTaskMutation } from "../data/mutations/taskMutations";
import { useJobQuery } from "../data/queries/jobQueries";
import { useTaskQuery } from "../data/queries/taskQueries";
import { JobServiceTaskBaseType } from "../domain/task";
import { CustomerNameText } from "./CustomerNameText";

const DriverDrivingBackToSite: FC<JobServiceTaskBaseType> = ({
  jobId,
  serviceId,
  taskId,
}) => {
  const { t } = useTranslator();

  const { data: jobData } = useJobQuery({ jobId });
  const { data: taskData } = useTaskQuery({ jobId, serviceId, taskId });

  const {
    mutate: updateTask,
    isPending: isUpdatingTask,
    variables,
  } = useUpdateTaskMutation();

  const { mutate: updateService, isPending: isUpdatingService } =
    useUpdateServiceMutation();

  if (!jobData || !taskData) {
    return null;
  }

  const contactName = jobData.site?.contact?.name;
  const contactPhone = jobData.site?.contact?.phone;

  const onNextClick = () => {
    updateService({
      serviceId,
      jobId,
      driverFlowStatus: TaskDriverStatusEnum.TAKING_PHOTO_DROPPED_BIN,
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus: TaskDriverStatusEnum.TAKING_PHOTO_DROPPED_BIN,
    });
  };

  const isLoading = isUpdatingTask || isUpdatingService;

  const isNextButtonLoading =
    isLoading &&
    variables?.driverFlowStatus ===
      TaskDriverStatusEnum.TAKING_PHOTO_DROPPED_BIN;

  const isPreviousButtonLoading =
    isLoading &&
    variables?.driverFlowStatus ===
      TaskDriverStatusEnum.FILLING_WEIGHT_SURCHARGE;

  const goToPreviousStep = () => {
    updateService({
      jobId,
      serviceId,
      driverFlowStatus: TaskDriverStatusEnum.FILLING_WEIGHT_SURCHARGE,
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus: TaskDriverStatusEnum.FILLING_WEIGHT_SURCHARGE,
    });
  };

  return (
    <div className="flex flex-col h-full w-full">
      <div className="flex flex-grow w-full flex-col">
        <DsText variant="pageTitle">
          {t("driver_driving_back_to_site.title")}
        </DsText>

        <DsDivider className="mt-3" />
        <DsDataDisplay
          title="driver_task_details.customer"
          data={
            <CustomerNameText
              jobId={jobId}
              serviceId={serviceId}
              variant="dataDisplayValue"
              className="font-bold"
            />
          }
        />

        <DsDataDisplay
          title="driver_task_details.job_site"
          data={<JobSiteLabel jobSiteAddress={taskData.address} />}
          dataClassName="font-bold"
        />

        <DsDataDisplay
          title="driver_task_details.job_contact"
          data={
            <PhoneCallButton
              phoneNumber={contactPhone?.toString() ?? ""}
              title={contactName!}
            />
          }
        />
      </div>
      <DriverFlowBottomButton
        isNextButtonLoading={isNextButtonLoading}
        onNextButtonClick={onNextClick}
        nextButtonTitle="driver_driving_back_to_site.next_button"
        isPreviousButtonLoading={isPreviousButtonLoading}
        onPreviousButtonClick={goToPreviousStep}
        previousButtonTitle="driver_driving_back_to_site.back_button"
      />
    </div>
  );
};

export { DriverDrivingBackToSite };
